import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Edit2, TrendingUp } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { createPortal } from 'react-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card } from '../ui';
import { getDishesBySite, getPricingAnalytics, getPricingPreview, getPricingRules, getPricingSignals, refreshPricesNow, updatePricingSignals } from './pricingApi';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const formatDateForAPI = (date) => date.toISOString().split('T')[0];
const formatDate = (date) =>
  date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
const getCurrentDate = () => new Date();

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom Range' },
];

const demandIntensityLabel = (value) => {
  if (value == null) return '—';
  if (value >= 75) return 'High';
  if (value >= 50) return 'Medium';
  if (value >= 25) return 'Low';
  return 'Very Low';
};

const DynamicDemandPricingScreen = () => {
  // Log screen view
  useScreenLogging('DynamicDemandPricingScreen', {
    component: 'DynamicDemandPricingScreen',
    screenType: 'pricing-dashboard',
  });

  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState('');
  const [period, setPeriod] = useState('weekly');
  const [selectedDate, setSelectedDate] = useState(formatDateForAPI(getCurrentDate()));
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedDishId, setSelectedDishId] = useState('');
  const [dishes, setDishes] = useState([]);
  const [dishesLoading, setDishesLoading] = useState(false);

  const [signals, setSignals] = useState({
    competitor_price_index: '',
    event_weather_index: '',
  });
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [signalsSaving, setSignalsSaving] = useState(false);
  const [signalsMessage, setSignalsMessage] = useState(null);
  const [signalsEditOpen, setSignalsEditOpen] = useState(false);
  const [rulesCheck, setRulesCheck] = useState(null);
  const [justSavedSignals, setJustSavedSignals] = useState(null); // Track recently saved signals to prevent overwrite
  const justSavedSignalsRef = useRef(null); // Ref to always have latest value in closures
  const skipNextDashboardLoadRef = useRef(false); // Flag to skip dashboard load immediately after saving
  const signalsSaveTimeRef = useRef(null); // Timestamp when signals were saved
  

  const fetchSites = useCallback(async () => {
    setIsSitesLoading(true);
    try {
      const response = await getAllSites();
      const list = Array.isArray(response?.sites)
        ? response.sites
        : Array.isArray(response)
        ? response
        : [];
      const sorted = [...list].sort((a, b) =>
        (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
      );
      setSites(sorted);
    } catch (e) {
      console.error('Pricing: fetch sites error', e);
      setSites([]);
    } finally {
      setIsSitesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  // Load dishes for selected site (for per-dish analytics filter)
  const fetchDishes = useCallback(async () => {
    if (!selectedSite) {
      setDishes([]);
      setSelectedDishId('');
      return;
    }
    setDishesLoading(true);
    try {
      const result = await getDishesBySite(selectedSite);
      const list = Array.isArray(result?.data) ? result.data : result?.data?.dishes ?? [];
      setDishes(Array.isArray(list) ? list : []);
      setSelectedDishId((prev) => {
        const ids = (Array.isArray(list) ? list : []).map((d) => d.dish_id ?? d.id ?? d._id).filter(Boolean);
        return ids.includes(prev) ? prev : '';
      });
    } catch (e) {
      console.error('Pricing: fetch dishes error', e);
      setDishes([]);
      setSelectedDishId('');
    } finally {
      setDishesLoading(false);
    }
  }, [selectedSite]);

  useEffect(() => {
    fetchDishes();
  }, [fetchDishes]);

  // Transform API response to match component's expected data structure
  // API returns: {site_id, current: {...}, history: Array(...)}
  // history items have: {time_bucket, current_demand_intensity, updated_at, ...}
  // current has: {current_demand_intensity, competitor_price_index, event_weather_index, suggested_price, ...}
  // Also handles root-level fields like st_run_at, current_demand_intensity, etc.
  // rulesCheck: optional object with {activeAuto: number, rules: Array} to use if backend doesn't provide count
  const transformAnalyticsData = (apiData, rulesCheck = null, selectedSiteId = null) => {
    if (!apiData) return null;

    // If the data already has the expected structure, return as-is
    if (apiData.summary && apiData.price_series && apiData.demand_series) {
      return apiData;
    }

    // Handle root-level fields (e.g., st_run_at, current_demand_intensity at root)
    const current = apiData.current || {};
    // Merge root-level fields into current if they exist at root
    if (apiData.st_run_at && !current.last_run_at && !current.st_run_at) {
      current.st_run_at = apiData.st_run_at;
    }
    if (apiData.current_demand_intensity != null && current.current_demand_intensity == null) {
      current.current_demand_intensity = apiData.current_demand_intensity;
    }
    if (apiData.competitor_price_index != null && current.competitor_price_index == null) {
      current.competitor_price_index = apiData.competitor_price_index;
    }
    if (apiData.event_weather_index != null && current.event_weather_index == null) {
      current.event_weather_index = apiData.event_weather_index;
    }
    if (apiData.avg_current_vs_base_percent != null && current.avg_current_vs_base_percent == null) {
      current.avg_current_vs_base_percent = apiData.avg_current_vs_base_percent;
    }
    if (apiData.dishes_auto_pricing_count != null && current.dishes_auto_pricing_count == null) {
      current.dishes_auto_pricing_count = apiData.dishes_auto_pricing_count;
    }

    const history = apiData.history || [];

    // Build demand series from history array (history has current_demand_intensity and time_bucket)
    const demandLabels = [];
    const demandIntensity = [];
    const forecastDemand = [];

    // Filter out empty history items, filter by site_id if selected, and sort by time_bucket (oldest first for chart)
    let filteredHistory = history.filter(item => item && item.time_bucket);
    
    // Filter by site_id if a site is selected
    if (selectedSiteId && filteredHistory.length > 0) {
      const apiSiteId = apiData.site_id;
      // If API response has site_id that doesn't match, clear history
      if (apiSiteId && apiSiteId !== selectedSiteId) {
        filteredHistory = [];
      } else {
        // Filter history items that have site_id field to match selected site
        filteredHistory = filteredHistory.filter(item => {
          const itemSiteId = item.site_id ?? item.siteId ?? item.site;
          // If item has no site_id, include it (might be site-agnostic or matches API site_id)
          // If item has site_id, it must match selected site
          return !itemSiteId || itemSiteId === selectedSiteId;
        });
      }
    }
    
    const validHistory = filteredHistory.sort((a, b) => {
      const timeA = new Date(a.time_bucket).getTime();
      const timeB = new Date(b.time_bucket).getTime();
      return timeA - timeB; // Oldest first for chart
    });
    
    // Log first history item to see its structure
    if (validHistory.length > 0) {
      const firstItem = validHistory[0];
      console.log('📊 First history item structure:', {
        time_bucket: firstItem.time_bucket,
        hasPrices: !!firstItem.prices,
        pricesLength: firstItem.prices?.length,
        current_price: firstItem.current_price,
        suggested_price: firstItem.suggested_price,
        base_price: firstItem.base_price,
        forecast_demand: firstItem.forecast_demand,
        forecast_demand_intensity: firstItem.forecast_demand_intensity,
        forecast_demand_type: typeof firstItem.forecast_demand,
        forecast_demand_intensity_type: typeof firstItem.forecast_demand_intensity,
        allKeys: Object.keys(firstItem),
      });
    }

    // Get the latest history item (most recent time_bucket) for fallback values
    const latestHistory = validHistory.length > 0 
      ? validHistory[validHistory.length - 1] // Last item is newest (after sorting oldest first)
      : null;

    // Extract summary from current data, fallback to latest history if current is null
    // If backend doesn't provide dishes_auto_pricing_count, use the count from rulesCheck
    let dishesAutoPricingCount = current.dishes_auto_pricing_count || current.auto_pricing_count;
    if (dishesAutoPricingCount == null || dishesAutoPricingCount === 0) {
      // Fallback to rules check if available
      if (rulesCheck && rulesCheck.activeAuto > 0) {
        dishesAutoPricingCount = rulesCheck.activeAuto;
        console.log('📊 Using rules check count for dishes_auto_pricing_count:', dishesAutoPricingCount);
      } else if (current.dishes && Array.isArray(current.dishes)) {
        // Last fallback: count dishes in current.dishes array
        dishesAutoPricingCount = current.dishes.length;
      } else {
        dishesAutoPricingCount = 0;
      }
    }

    const summary = {
      dishes_auto_pricing_count: dishesAutoPricingCount,
      avg_current_vs_base_percent: current.avg_current_vs_base_percent || current.avg_price_change_percent || null,
      last_run_at: current.last_run_at || current.st_run_at || current.last_updated || current.updated_at || 
        (latestHistory ? latestHistory.updated_at : null),
      // Use current value, or fallback to latest history, or null
      current_demand_intensity: current.current_demand_intensity ?? 
        (latestHistory ? latestHistory.current_demand_intensity : null) ?? null,
      competitor_price_index: current.competitor_price_index ?? null,
      event_weather_index: current.event_weather_index ?? null,
    };

    validHistory.forEach((item) => {
      const timeBucket = item.time_bucket;
      if (!timeBucket) return;

      // Format label for display (e.g., "10 Feb 09:00" or "10 Feb")
      let label = '';
      try {
        const date = new Date(timeBucket);
        // Show time if multiple hours, otherwise just date
        const hours = date.getHours();
        label = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
        if (validHistory.length > 1) {
          label += ` ${String(hours).padStart(2, '0')}:00`;
        }
      } catch (e) {
        // Fallback: extract hour from time_bucket string
        const match = timeBucket.match(/T(\d{2}):/);
        if (match) {
          label = timeBucket.split('T')[0] + ' ' + match[1] + ':00';
        } else {
          label = String(timeBucket).split('T')[0];
        }
      }

      // Extract demand data (history[].forecast_demand from backend; fallbacks for legacy shapes)
      if (item.current_demand_intensity != null) {
        demandLabels.push(label);
        demandIntensity.push(item.current_demand_intensity);
        // Try multiple property names for forecast (including forecast_demand_intensity)
        // Check if forecast_demand exists (even if null, we want to know)
        // Priority: forecast_demand > forecast_demand_intensity > forecast > forecasted_demand
        const forecast = item.forecast_demand ?? item.forecast_demand_intensity ?? item.forecast ?? item.forecasted_demand;
        // Handle both number and object types
        let forecastValue = null;
        if (forecast != null) {
          if (typeof forecast === 'number') {
            forecastValue = forecast;
          } else if (typeof forecast === 'object' && forecast !== null) {
            // Try to extract from object
            forecastValue = forecast.value ?? forecast.intensity ?? forecast.demand;
          }
        }
        // Always push (even if null) to maintain array alignment with demandLabels
        forecastDemand.push(forecastValue);
        
        // Debug logging for first few items
        if (validHistory.indexOf(item) < 3) {
          console.log(`📊 Forecast extraction [${validHistory.indexOf(item)}]:`, {
            time_bucket: item.time_bucket,
            forecast_demand: item.forecast_demand,
            forecast_demand_intensity: item.forecast_demand_intensity,
            extractedValue: forecastValue,
            willPush: true,
          });
        }
        
        // Debug: Log if we found forecast but it's null
        if (item.forecast_demand != null && forecastValue == null) {
          console.warn('📊 Forecast exists but extraction failed:', {
            forecast_demand: item.forecast_demand,
            forecast_demand_type: typeof item.forecast_demand,
            extractedValue: forecastValue,
          });
        }
        
        // Log first item to debug forecast extraction
        if (validHistory.indexOf(item) === 0) {
          console.log('📊 Forecast extraction from first history item:', {
            forecast_demand: item.forecast_demand,
            forecast_demand_type: typeof item.forecast_demand,
            forecast_demand_intensity: item.forecast_demand_intensity,
            forecast_demand_intensity_type: typeof item.forecast_demand_intensity,
            forecast: item.forecast,
            forecasted_demand: item.forecasted_demand,
            extractedValue: forecastValue,
            allKeys: Object.keys(item),
            itemValues: {
              forecast_demand: item.forecast_demand,
              forecast_demand_intensity: item.forecast_demand_intensity,
            },
          });
        }
      } else {
        // Even if no current_demand_intensity, check if forecast exists and add it
        // This ensures forecast line shows even if demand intensity is missing
        const forecast = item.forecast_demand ?? item.forecast_demand_intensity ?? item.forecast ?? item.forecasted_demand;
        let forecastValue = null;
        if (forecast != null) {
          if (typeof forecast === 'number') {
            forecastValue = forecast;
          } else if (typeof forecast === 'object' && forecast !== null) {
            forecastValue = forecast.value ?? forecast.intensity ?? forecast.demand;
          }
        }
        // Always add forecast data if it exists, even without current_demand_intensity
        if (forecastValue != null) {
          // Add label and null demand intensity (forecast will be in separate dataset)
          demandLabels.push(label);
          demandIntensity.push(null); // Keep null for demand intensity, use separate forecast dataset
          forecastDemand.push(forecastValue);
          
          // Debug logging
          if (validHistory.indexOf(item) < 3) {
            console.log(`📊 Forecast-only extraction [${validHistory.indexOf(item)}]:`, {
              time_bucket: item.time_bucket,
              forecast_demand: item.forecast_demand,
              extractedForecast: forecastValue,
              addedToChart: true,
            });
          }
        }
      }
    });

    // For price series, check if we have price data in history or current
    // History items might have: current_price, suggested_price, base_price, or prices array
    const priceLabels = [];
    const priceCurrent = [];
    const priceSuggested = [];
    const priceBase = [];

    // Check if history items have price data
    let hasPriceData = false;
    validHistory.forEach((item) => {
      const timeBucket = item.time_bucket;
      if (!timeBucket) return;

      // Format label same as demand series
      let label = '';
      try {
        const date = new Date(timeBucket);
        const hours = date.getHours();
        label = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
        if (validHistory.length > 1) {
          label += ` ${String(hours).padStart(2, '0')}:00`;
        }
      } catch (e) {
        const match = timeBucket.match(/T(\d{2}):/);
        if (match) {
          label = timeBucket.split('T')[0] + ' ' + match[1] + ':00';
        } else {
          label = String(timeBucket).split('T')[0];
        }
      }

      // Check for price data in history item (current_price, base_price, suggested_price; may be null if no rules)
      const extractPrice = (v) => {
        if (v == null) return null;
        if (typeof v === 'number') return Math.round(v);
        if (typeof v === 'object' && v !== null) {
          // Handle object with price property (e.g., {price: 100}, {value: 100}, {amount: 100})
          const price = v.price ?? v.value ?? v.amount ?? v.suggested_price ?? v.current_price ?? v.base_price;
          if (typeof price === 'number') return Math.round(price);
          // If it's nested, try one more level
          if (typeof price === 'object' && price !== null) {
            const nestedPrice = price.price ?? price.value ?? price.amount;
            if (typeof nestedPrice === 'number') return Math.round(nestedPrice);
          }
        }
        return null;
      };
      
      if (item.prices && Array.isArray(item.prices) && item.prices.length > 0) {
        const sumCurrent = item.prices.reduce((sum, p) => sum + (extractPrice(p.current_price ?? p.current) ?? 0), 0);
        const sumSuggested = item.prices.reduce((sum, p) => sum + (extractPrice(p.suggested_price ?? p.suggested) ?? 0), 0);
        const sumBase = item.prices.reduce((sum, p) => sum + (extractPrice(p.base_price ?? p.base) ?? 0), 0);
        const n = item.prices.length;
        priceLabels.push(label);
        priceCurrent.push(n ? Math.round(sumCurrent / n) : null);
        priceSuggested.push(n ? Math.round(sumSuggested / n) : null);
        priceBase.push(n ? Math.round(sumBase / n) : null);
        hasPriceData = true;
      } else if (item.current_price != null || item.suggested_price != null || item.base_price != null) {
        // Single price values in history item (handle nulls: show point only when value exists)
        priceLabels.push(label);
        priceCurrent.push(extractPrice(item.current_price));
        priceSuggested.push(extractPrice(item.suggested_price));
        priceBase.push(extractPrice(item.base_price));
        hasPriceData = true;
      }
    });

    // If no price data in history, check current object
    const numVal = (v) => {
      if (v == null) return null;
      if (typeof v === 'number') return Math.round(v);
      if (typeof v === 'object' && v !== null) {
        // Handle object with price property (e.g., {price: 100}, {value: 100}, {amount: 100})
        const price = v.price ?? v.value ?? v.amount ?? v.suggested_price ?? v.current_price ?? v.base_price;
        if (typeof price === 'number') return Math.round(price);
        // If it's nested, try one more level
        if (typeof price === 'object' && price !== null) {
          const nestedPrice = price.price ?? price.value ?? price.amount;
          if (typeof nestedPrice === 'number') return Math.round(nestedPrice);
        }
      }
      return null;
    };
    
    if (!hasPriceData) {
      if (current.dishes && Array.isArray(current.dishes) && current.dishes.length > 0) {
        priceLabels.push(...demandLabels);
        const dishes = current.dishes;
        const sumCur = dishes.reduce((s, p) => s + (numVal(p.current_price ?? p.current) ?? 0), 0);
        const sumSug = dishes.reduce((s, p) => s + (numVal(p.suggested_price ?? p.suggested) ?? 0), 0);
        const sumBase = dishes.reduce((s, p) => s + (numVal(p.base_price ?? p.base) ?? 0), 0);
        const n = dishes.length;
        const avgCurrent = n ? sumCur / n : null;
        const avgSuggested = n ? sumSug / n : null;
        const avgBase = n ? sumBase / n : null;

        demandLabels.forEach(() => {
          priceCurrent.push(numVal(avgCurrent));
          priceSuggested.push(numVal(avgSuggested));
          priceBase.push(numVal(avgBase));
        });
        hasPriceData = (avgCurrent != null && avgCurrent > 0) || (avgSuggested != null && avgSuggested > 0) || (avgBase != null && avgBase > 0);
      } else {
        // Check current object for price fields (may be objects or numbers)
        const currentPrice = numVal(current.current_price);
        let suggestedPrice = numVal(current.suggested_price);
        const basePrice = numVal(current.base_price);
        
        // If suggested_price is an object, try to extract from it
        if (suggestedPrice == null && current.suggested_price && typeof current.suggested_price === 'object') {
          console.log('📊 suggested_price is an object, attempting extraction:', {
            suggested_price: current.suggested_price,
            keys: Object.keys(current.suggested_price),
            isEmpty: Object.keys(current.suggested_price).length === 0,
          });
          // Try common property names
          suggestedPrice = numVal(current.suggested_price.price ?? current.suggested_price.value ?? current.suggested_price.amount);
        }
        
        if (currentPrice != null || suggestedPrice != null || basePrice != null) {
          priceLabels.push(...demandLabels);
          demandLabels.forEach(() => {
            priceCurrent.push(currentPrice);
            priceSuggested.push(suggestedPrice);
            priceBase.push(basePrice);
          });
          hasPriceData = true;
          console.log('📊 Extracted prices from current object:', {
            currentPrice,
            suggestedPrice,
            basePrice,
            suggestedPriceSource: current.suggested_price,
          });
        } else {
          console.log('📊 No prices found in current object:', {
            current_price: current.current_price,
            suggested_price: current.suggested_price,
            base_price: current.base_price,
            suggested_price_type: typeof current.suggested_price,
            suggested_price_keys: current.suggested_price ? Object.keys(current.suggested_price) : null,
          });
        }
      }
    }

    // If still no price data, use demand labels and create arrays of nulls/zeros
    // This ensures the chart always shows the lines (even if empty) so users know what to expect
    if (!hasPriceData && demandLabels.length > 0) {
      priceLabels.push(...demandLabels);
      // Use null instead of 0 so Chart.js skips the points but still shows the line in legend
      demandLabels.forEach(() => {
        priceCurrent.push(null);
        priceSuggested.push(null);
        priceBase.push(null);
      });
    }

    // Ensure price series has the same number of data points as demand series
    // If price data is incomplete, pad with nulls or use demand labels
    const finalPriceLabels = priceLabels.length > 0 && priceLabels.length === demandLabels.length 
      ? priceLabels 
      : demandLabels.length > 0 
        ? demandLabels 
        : ['No data'];
    
    // Pad price arrays to match demand series length
    const padPriceArray = (arr, targetLength) => {
      if (arr.length >= targetLength) return arr;
      const padded = [...arr];
      while (padded.length < targetLength) {
        padded.push(null);
      }
      return padded;
    };

    // If we have rules but no price data, try to create base price data from rules
    let finalPriceCurrent = padPriceArray(priceCurrent.length > 0 ? priceCurrent : [], finalPriceLabels.length);
    let finalPriceSuggested = padPriceArray(priceSuggested.length > 0 ? priceSuggested : [], finalPriceLabels.length);
    let finalPriceBase = padPriceArray(priceBase.length > 0 ? priceBase : [], finalPriceLabels.length);
    
    // If no price data but we have rules, use prices from rules
    if (rulesCheck && rulesCheck.rules && rulesCheck.rules.length > 0) {
      const activeAutoRules = rulesCheck.rules.filter(r => r.is_active !== false && r.pricing_mode === 'auto');
      if (activeAutoRules.length > 0) {
        // Calculate average base price
        const avgBasePrice = Math.round(
          activeAutoRules.reduce((sum, r) => sum + (r.base_price || 0), 0) / activeAutoRules.length
        );
        
        // Use base price for base line if missing
        if (finalPriceBase.every(v => v == null)) {
          console.log('📊 Using average base price from rules:', avgBasePrice);
          finalPriceBase = finalPriceLabels.map(() => avgBasePrice);
        }
        
        // For current/suggested prices, if missing, we could use base price as a placeholder
        // But only if they're completely empty (all nulls)
        if (finalPriceCurrent.every(v => v == null) && finalPriceSuggested.every(v => v == null)) {
          console.log('📊 No current/suggested prices found. Base price shown from rules.');
          // Don't duplicate base price for current/suggested - leave them null so user knows they're missing
        }
      }
    }

    const transformed = {
      summary,
      price_series: {
        labels: finalPriceLabels,
        // Always include arrays (even if all nulls) so Chart.js can render the lines
        current: finalPriceCurrent,
        suggested: finalPriceSuggested,
        base: finalPriceBase,
      },
      demand_series: {
        labels: demandLabels.length > 0 ? demandLabels : ['No data'],
        demand_intensity: demandIntensity.length > 0 ? demandIntensity : [0],
        // Always include forecast_demand array (even if all nulls) so Chart.js can render the line
        // Chart.js will skip null values but still show the line in the legend
        forecast_demand: forecastDemand.length > 0 ? forecastDemand : [],
      },
      recent_price_changes: (() => {
        const changes = apiData.recent_price_changes || apiData.price_changes || apiData.recent_changes || [];
        console.log('📊 Recent price changes extraction:', {
          hasRecentPriceChanges: !!apiData.recent_price_changes,
          hasPriceChanges: !!apiData.price_changes,
          hasRecentChanges: !!apiData.recent_changes,
          extractedCount: changes.length,
          sample: changes.slice(0, 2),
          allKeys: Object.keys(apiData).filter(k => k.toLowerCase().includes('change') || k.toLowerCase().includes('price')),
        });
        return changes;
      })(),
    };

    console.log('📊 Final transformed data:', {
      priceLabels: transformed.price_series.labels.length,
      priceCurrent: transformed.price_series.current.filter(v => v != null).length,
      priceSuggested: transformed.price_series.suggested.filter(v => v != null).length,
      priceBase: transformed.price_series.base.filter(v => v != null).length,
      demandLabels: transformed.demand_series.labels.length,
      demandIntensity: transformed.demand_series.demand_intensity.filter(v => v != null).length,
      forecastDemand: transformed.demand_series.forecast_demand.filter(v => v != null).length,
      forecastDemandArray: transformed.demand_series.forecast_demand,
      forecastDemandRaw: forecastDemand,
      recentPriceChanges: transformed.recent_price_changes.length,
      recentPriceChangesSample: transformed.recent_price_changes.slice(0, 2),
    });

    return transformed;
  };

  // Check if pricing rules exist for the selected site
  const checkPricingRules = useCallback(async () => {
    if (!selectedSite) {
      setRulesCheck(null);
      return;
    }
    try {
      const rulesResult = await getPricingRules({ site_id: selectedSite });
      if (rulesResult.success && rulesResult.data) {
        const rulesList = Array.isArray(rulesResult.data) ? rulesResult.data : [rulesResult.data];
        const activeAutoRules = rulesList.filter(
          (r) => r.is_active !== false && r.pricing_mode === 'auto'
        );
        setRulesCheck({
          total: rulesList.length,
          activeAuto: activeAutoRules.length,
          rules: rulesList,
        });
        console.log('📋 Pricing Rules Check:', {
          site_id: selectedSite,
          totalRules: rulesList.length,
          activeAutoRules: activeAutoRules.length,
          rules: rulesList.map((r) => ({
            dish_id: r.dish_id,
            site_id: r.site_id,
            pricing_mode: r.pricing_mode,
            is_active: r.is_active,
            base_price: r.base_price,
          })),
        });
      } else {
        setRulesCheck({ total: 0, activeAuto: 0, rules: [] });
        console.warn('📋 No pricing rules found for site:', selectedSite);
      }
    } catch (e) {
      console.error('📋 Failed to check pricing rules:', e);
      setRulesCheck(null);
    }
  }, [selectedSite]);

  const loadDashboard = useCallback(async () => {
    // Skip loading if we just saved signals (to prevent overwriting saved values)
    if (skipNextDashboardLoadRef.current) {
      console.log('📊 Skipping loadDashboard - signals were just saved');
      skipNextDashboardLoadRef.current = false;
      return;
    }
    
    setIsLoading(true);
    setError(null);
    let currentRulesCheck = null;
    try {
      // Check pricing rules first for debugging and to get count
      await checkPricingRules();
      // Get the current rulesCheck state (we'll use it in transformation)
      // Note: rulesCheck state might not be updated yet, so we'll fetch it again if needed
      if (selectedSite) {
        try {
          const rulesResult = await getPricingRules({ site_id: selectedSite });
          if (rulesResult.success && rulesResult.data) {
            const rulesList = Array.isArray(rulesResult.data) ? rulesResult.data : [rulesResult.data];
            const activeAutoRules = rulesList.filter(
              (r) => r.is_active !== false && r.pricing_mode === 'auto'
            );
            currentRulesCheck = {
              total: rulesList.length,
              activeAuto: activeAutoRules.length,
              rules: rulesList,
            };
          }
        } catch (e) {
          console.warn('📋 Could not fetch rules for transformation:', e);
        }
      }

      // Backend API for pricing analytics expects: site_id, time_bucket
      // time_bucket format: "YYYY-MM-DDTHH:MM" (e.g., "2026-02-03T10:00")
      // We use the current hour for the time_bucket, or 00:00 if using a past date
      const effectiveDate = selectedDate || formatDateForAPI(getCurrentDate());
      const now = getCurrentDate();
      const isToday = effectiveDate === formatDateForAPI(now);
      // Use current hour if today, otherwise use 00:00
      const hour = isToday ? String(now.getHours()).padStart(2, '0') : '00';
      const minute = isToday ? String(now.getMinutes()).padStart(2, '0') : '00';
      const time_bucket = `${effectiveDate}T${hour}:${minute}`;

      console.log('📊 Loading dashboard with:', {
        site_id: selectedSite,
        dish_id: selectedDishId,
        time_bucket,
      });

      const result = await getPricingAnalytics({
        site_id: selectedSite || undefined,
        dish_id: selectedDishId || undefined,
        time_bucket,
      });
      if (result.success && result.data) {
        console.log('📊 Analytics API Response:', {
          rawData: result.data,
          dishes_auto_pricing_count: result.data?.current?.dishes_auto_pricing_count ?? result.data?.dishes_auto_pricing_count,
          hasHistory: !!result.data?.history?.length,
          historyLength: result.data?.history?.length,
          currentObject: result.data?.current,
          currentSuggestedPrice: result.data?.current?.suggested_price,
          currentSuggestedPriceKeys: result.data?.current?.suggested_price ? Object.keys(result.data.current.suggested_price) : null,
          currentCurrentPrice: result.data?.current?.current_price,
          currentBasePrice: result.data?.current?.base_price,
          currentDishes: result.data?.current?.dishes,
          firstHistoryItem: result.data?.history?.[0],
          lastHistoryItem: result.data?.history?.[result.data?.history?.length - 1],
          recentPriceChanges: result.data?.recent_price_changes || result.data?.price_changes,
          hasRecentChanges: !!(result.data?.recent_price_changes || result.data?.price_changes),
        });
        // Pass currentRulesCheck to transformation so it can use the count if backend doesn't provide it
        // Also pass selectedSite to filter data by site
        const transformedData = transformAnalyticsData(result.data, currentRulesCheck, selectedSite);
        if (transformedData) {
          console.log('📊 Transformed Data Summary:', {
            dishes_auto_pricing_count: transformedData.summary?.dishes_auto_pricing_count,
            hasPriceSeries: !!transformedData.price_series,
            priceSeriesLength: transformedData.price_series?.labels?.length,
            hasDemandSeries: !!transformedData.demand_series,
            demandSeriesLength: transformedData.demand_series?.labels?.length,
          });
          
          // If no current/suggested prices found, try to get them from preview API
          const hasCurrentPrices = transformedData.price_series?.current?.some(v => v != null);
          const hasSuggestedPrices = transformedData.price_series?.suggested?.some(v => v != null);
          
          if ((!hasCurrentPrices || !hasSuggestedPrices) && selectedSite) {
            console.log('📊 Missing price data, fetching from preview API...', {
              site_id: selectedSite,
              dish_id: selectedDishId || undefined,
            });
            // Fetch preview data in background to get current/suggested prices
            // Include dish_id if a specific dish is selected
            getPricingPreview({ 
              site_id: selectedSite,
              dish_id: selectedDishId || undefined,
            })
              .then((previewResult) => {
                console.log('📊 Preview API Response:', {
                  success: previewResult.success,
                  data: previewResult.data,
                  preview: previewResult.data?.preview,
                  items: previewResult.data?.items,
                  previewLength: previewResult.data?.preview?.length,
                  itemsLength: previewResult.data?.items?.length,
                  firstItem: previewResult.data?.preview?.[0] || previewResult.data?.items?.[0],
                });
                
                if (previewResult.success && previewResult.data) {
                  // Handle different response structures - check for 'preview' or 'items'
                  let items = Array.isArray(previewResult.data.preview) 
                    ? previewResult.data.preview 
                    : Array.isArray(previewResult.data.items) 
                      ? previewResult.data.items 
                      : Array.isArray(previewResult.data) 
                        ? previewResult.data 
                        : [];
                  
                  // Filter by dish_id if a specific dish is selected
                  if (selectedDishId && items.length > 0) {
                    items = items.filter(item => {
                      const itemDishId = item.dish_id ?? item.id ?? item._id;
                      return itemDishId === selectedDishId;
                    });
                    console.log('📊 Filtered preview items by dish_id:', {
                      selectedDishId,
                      originalCount: previewResult.data.preview?.length || previewResult.data.items?.length || previewResult.data?.length || 0,
                      filteredCount: items.length,
                    });
                  }
                  
                  if (items.length > 0) {
                    // Calculate averages from preview, handling different property names
                    // Note: Preview API may not have current_price, so use base_price as fallback
                    const validCurrent = items.filter(item => item.current_price != null || item.current != null || item.base_price != null);
                    const validSuggested = items.filter(item => item.suggested_price != null || item.suggested != null);
                    const validBase = items.filter(item => item.base_price != null || item.base != null);
                    
                    // For current price: use current_price if available, otherwise use base_price
                    const avgCurrent = validCurrent.length > 0
                      ? validCurrent.reduce((sum, item) => {
                          const price = item.current_price ?? item.current ?? item.base_price ?? item.base ?? 0;
                          return sum + price;
                        }, 0) / validCurrent.length
                      : null;
                    
                    const avgSuggested = validSuggested.length > 0
                      ? validSuggested.reduce((sum, item) => sum + (item.suggested_price ?? item.suggested ?? 0), 0) / validSuggested.length
                      : null;
                    
                    // Also calculate avg base price from preview (might be more accurate than rules)
                    const avgBaseFromPreview = validBase.length > 0
                      ? validBase.reduce((sum, item) => sum + (item.base_price ?? item.base ?? 0), 0) / validBase.length
                      : null;
                    
                    console.log('📊 Got prices from preview API:', {
                      avgCurrent: avgCurrent ? Math.round(avgCurrent) : null,
                      avgSuggested: avgSuggested ? Math.round(avgSuggested) : null,
                      avgBaseFromPreview: avgBaseFromPreview ? Math.round(avgBaseFromPreview) : null,
                      itemsCount: items.length,
                      validCurrentCount: validCurrent.length,
                      validSuggestedCount: validSuggested.length,
                      validBaseCount: validBase.length,
                      sampleItem: items[0],
                    });
                    
                    // Create a mapping from dish_id to dish_name from the dishes state
                    const dishIdToName = {};
                    dishes.forEach(dish => {
                      const id = dish.dish_id ?? dish.id ?? dish._id;
                      const name = dish.dish_name ?? dish.name;
                      if (id && name) {
                        dishIdToName[id] = name;
                      }
                    });
                    
                    // Generate recent price changes from preview data
                    // Compare current_price or suggested_price with base_price to find changes
                    // Also filter by site_id to ensure only changes for the selected site are shown
                    const recentChangesFromPreview = items
                      .filter(item => {
                        // Filter by site_id if a site is selected
                        if (selectedSite) {
                          const itemSiteId = item.site_id ?? item.siteId ?? item.site;
                          if (itemSiteId && itemSiteId !== selectedSite) {
                            return false; // Skip items from other sites
                          }
                        }
                        
                        const base = item.base_price ?? item.base ?? null;
                        const current = item.current_price ?? item.current ?? null;
                        const suggested = item.suggested_price ?? item.suggested ?? null;
                        // Include if there's a price change (current or suggested differs from base)
                        return base != null && (current != null || suggested != null) && 
                               (current != base || suggested != base);
                      })
                      .map(item => {
                        const base = item.base_price ?? item.base ?? 0;
                        const current = item.current_price ?? item.current ?? base;
                        const suggested = item.suggested_price ?? item.suggested ?? base;
                        // Use suggested if it differs from base, otherwise use current
                        const newPrice = suggested != base ? suggested : current;
                        const oldPrice = base;
                        
                        // Get dish name from mapping, preview API, or fallback to ID
                        const dishId = item.dish_id ?? item.id;
                        const dishName = dishIdToName[dishId] 
                          || item.dish_name 
                          || item.name 
                          || dishId 
                          || 'Unknown dish';
                        
                        // Format time (use updated_at if available, otherwise "Recently")
                        let timeStr = 'Recently';
                        if (item.updated_at) {
                          try {
                            const updatedDate = new Date(item.updated_at);
                            const minsAgo = Math.round((Date.now() - updatedDate.getTime()) / 60000);
                            if (minsAgo < 60) {
                              timeStr = `${minsAgo} min ago`;
                            } else {
                              timeStr = updatedDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                            }
                          } catch (e) {
                            // Keep "Recently" if date parsing fails
                          }
                        }
                        
                        return {
                          dish_name: dishName,
                          dish_id: dishId,
                          old_price: oldPrice,
                          new_price: newPrice,
                          time: timeStr,
                          reason: item.reason || (suggested != base ? 'Auto-pricing' : 'Manual update'),
                        };
                      })
                      .slice(0, 10); // Limit to 10 most recent
                    
                    console.log('📊 Generated recent price changes from preview:', {
                      count: recentChangesFromPreview.length,
                      sample: recentChangesFromPreview.slice(0, 3),
                    });
                    
                    // Update the data with preview prices and recent changes
                    setData((prevData) => {
                      if (!prevData) return prevData;
                      const labels = prevData.price_series?.labels || [];
                      const updatedPriceSeries = {
                        ...prevData.price_series,
                        // Fill all labels with average prices from preview (only if we have valid data)
                        current: labels.map(() => avgCurrent ? Math.round(avgCurrent) : null),
                        suggested: labels.map(() => avgSuggested ? Math.round(avgSuggested) : null),
                        // Update base price from preview if available and current base is null
                        base: prevData.price_series.base.every(v => v == null) && avgBaseFromPreview
                          ? labels.map(() => Math.round(avgBaseFromPreview))
                          : prevData.price_series.base,
                        // Add a timestamp to force chart re-render
                        _previewUpdatedAt: Date.now(),
                      };
                      
                      console.log('📊 Updated price_series with preview data:', {
                        labelsCount: labels.length,
                        currentValues: updatedPriceSeries.current,
                        suggestedValues: updatedPriceSeries.suggested,
                        baseValues: updatedPriceSeries.base,
                        hasCurrent: updatedPriceSeries.current.some(v => v != null),
                        hasSuggested: updatedPriceSeries.suggested.some(v => v != null),
                      });
                      
                      return {
                        ...prevData,
                        price_series: updatedPriceSeries,
                        // Merge generated recent changes with any existing ones from API
                        recent_price_changes: recentChangesFromPreview.length > 0 
                          ? recentChangesFromPreview 
                          : (prevData.recent_price_changes || []),
                      };
                    });
                  } else {
                    console.warn('📊 Preview API returned no items:', previewResult);
                  }
                } else {
                  console.warn('📊 Preview API failed or returned no data:', previewResult);
                }
              })
              .catch((e) => {
                console.warn('📊 Failed to fetch preview prices (non-blocking):', e);
              });
          }
          
          setData(transformedData);
          // Fetch signals separately and merge into summary (analytics API doesn't return them)
          // This is non-blocking - graphs should render even if signals fail
          if (selectedSite) {
            // Don't await - let it run in background so it doesn't block rendering
            const fetchStartTime = Date.now(); // Record when fetch starts
            getPricingSignals({ site_id: selectedSite })
              .then((signalsResult) => {
                console.log('📊 Signals fetch result:', signalsResult);
                if (signalsResult.success && signalsResult.data) {
                  // API returns signals in data.current, not data directly
                  const signalsData = signalsResult.data.current || signalsResult.data;
                  
                  // Check if we just saved signals - if so, use the saved values instead of API values
                  // This prevents the API from overwriting values we just saved (API might have stale data)
                  // Use ref to get latest value in async callback - read it fresh right now
                  const currentSavedSignals = justSavedSignalsRef.current;
                  const saveTime = signalsSaveTimeRef.current;
                  
                  // If signals were saved AFTER this fetch started, ignore this merge
                  if (saveTime && saveTime > fetchStartTime) {
                    console.log('📊 Ignoring signals merge - signals were saved after fetch started:', {
                      fetchStartTime,
                      saveTime,
                      savedSignals: currentSavedSignals,
                    });
                    return; // Don't merge - this fetch is stale
                  }
                  
                  const shouldUseSavedValues = currentSavedSignals && 
                    (currentSavedSignals.competitor_price_index != null || currentSavedSignals.event_weather_index != null);
                  
                  // If we just saved signals, skip this merge entirely to prevent overwriting
                  if (shouldUseSavedValues) {
                    return; // Don't merge - saved values are already in the summary
                  }
                  
                  // Double-check ref again right before merging (defensive)
                  const finalCheckSavedSignals = justSavedSignalsRef.current;
                  if (finalCheckSavedSignals && (finalCheckSavedSignals.competitor_price_index != null || finalCheckSavedSignals.event_weather_index != null)) {
                    return; // Don't merge - saved values are already in the summary
                  }
                  
                  setData((prevData) => {
                    if (!prevData) return prevData;
                    // If we just saved signals, use those values instead of API values
                    // Otherwise use API values if available
                    const finalCompetitor = shouldUseSavedValues && currentSavedSignals?.competitor_price_index != null
                      ? currentSavedSignals.competitor_price_index
                      : (signalsData.competitor_price_index != null 
                          ? signalsData.competitor_price_index 
                          : prevData.summary.competitor_price_index);
                    const finalEvent = shouldUseSavedValues && currentSavedSignals?.event_weather_index != null
                      ? currentSavedSignals.event_weather_index
                      : (signalsData.event_weather_index != null 
                          ? signalsData.event_weather_index 
                          : prevData.summary.event_weather_index);
                    
                    const updated = {
                      ...prevData,
                      summary: {
                        ...prevData.summary,
                        competitor_price_index: finalCompetitor,
                        event_weather_index: finalEvent,
                      },
                    };
                    return updated;
                  });
                  // Also update signals state for the edit modal
                  // But check if we just saved signals - if so, use those values instead of API values
                  setSignals({
                    competitor_price_index: shouldUseSavedValues && currentSavedSignals.competitor_price_index != null
                      ? String(currentSavedSignals.competitor_price_index)
                      : (signalsData.competitor_price_index != null 
                          ? String(signalsData.competitor_price_index) 
                          : ''),
                    event_weather_index: shouldUseSavedValues && currentSavedSignals.event_weather_index != null
                      ? String(currentSavedSignals.event_weather_index)
                      : (signalsData.event_weather_index != null 
                          ? String(signalsData.event_weather_index) 
                          : ''),
                  });
                } else {
                  console.warn('📊 Signals fetch failed or no data (non-blocking):', signalsResult);
                }
              })
              .catch((e) => {
                console.warn('📊 Failed to load signals for summary (non-blocking):', e);
              });
          } else {
            console.log('📊 No selectedSite, skipping signals fetch');
          }
        } else {
          setError('API returned data in an unexpected format');
        }
      } else {
        if (result.data) {
          // Pass currentRulesCheck to transformation so it can use the count if backend doesn't provide it
          const transformedData = transformAnalyticsData(result.data, currentRulesCheck, selectedSite);
          if (transformedData) {
            setData(transformedData);
            // Fetch signals separately and merge into summary (non-blocking)
            if (selectedSite) {
              // Don't await - let it run in background so it doesn't block rendering
              const fetchStartTime = Date.now(); // Record when fetch starts
              getPricingSignals({ site_id: selectedSite })
                .then((signalsResult) => {
                  console.log('📊 Signals fetch result (else branch):', signalsResult);
                  if (signalsResult.success && signalsResult.data) {
                    // API returns signals in data.current, not data directly
                    const signalsData = signalsResult.data.current || signalsResult.data;
                    
                    // Check if we just saved signals - if so, use the saved values instead of API values
                    // Use ref to get latest value in async callback - read it fresh right now
                    const currentSavedSignals = justSavedSignalsRef.current;
                    const saveTime = signalsSaveTimeRef.current;
                    
                    // If signals were saved AFTER this fetch started, ignore this merge
                    if (saveTime && saveTime > fetchStartTime) {
                      console.log('📊 Ignoring signals merge (else branch) - signals were saved after fetch started:', {
                        fetchStartTime,
                        saveTime,
                        savedSignals: currentSavedSignals,
                      });
                      return; // Don't merge - this fetch is stale
                    }
                    
                    console.log('📊 Checking ref in loadDashboard signals merge (else branch):', {
                      refValue: currentSavedSignals,
                      refExists: !!currentSavedSignals,
                      competitor: currentSavedSignals?.competitor_price_index,
                      event: currentSavedSignals?.event_weather_index,
                      fetchStartTime,
                      saveTime,
                    });
                    
                    const shouldUseSavedValues = currentSavedSignals && 
                      (currentSavedSignals.competitor_price_index != null || currentSavedSignals.event_weather_index != null);
                    
                    // If we just saved signals, skip this merge entirely to prevent overwriting
                    if (shouldUseSavedValues) {
                      console.log('📊 Skipping signals merge in loadDashboard (else branch) - using saved values instead:', currentSavedSignals);
                      return; // Don't merge - saved values are already in the summary
                    }
                    
                    console.log('📊 Merging signals into summary (else branch):', {
                      competitor_price_index: signalsData.competitor_price_index,
                      event_weather_index: signalsData.event_weather_index,
                      currentSavedSignals,
                      shouldUseSavedValues,
                    });
                    // Double-check ref again right before merging (defensive)
                    const finalCheckSavedSignals = justSavedSignalsRef.current;
                    if (finalCheckSavedSignals && (finalCheckSavedSignals.competitor_price_index != null || finalCheckSavedSignals.event_weather_index != null)) {
                      console.log('📊 Final check: Skipping merge (else branch) - saved signals exist:', finalCheckSavedSignals);
                      return; // Don't merge - saved values are already in the summary
                    }
                    
                    setData((prevData) => {
                      if (!prevData) return prevData;
                      // If we just saved signals, use those values instead of API values
                      const updated = {
                        ...prevData,
                        summary: {
                          ...prevData.summary,
                          competitor_price_index: shouldUseSavedValues && currentSavedSignals.competitor_price_index != null
                            ? currentSavedSignals.competitor_price_index
                            : (signalsData.competitor_price_index != null 
                                ? signalsData.competitor_price_index 
                                : prevData.summary.competitor_price_index),
                          event_weather_index: shouldUseSavedValues && currentSavedSignals.event_weather_index != null
                            ? currentSavedSignals.event_weather_index
                            : (signalsData.event_weather_index != null 
                                ? signalsData.event_weather_index 
                                : prevData.summary.event_weather_index),
                        },
                      };
                      console.log('📊 Updated summary after merge (else branch):', updated.summary);
                      return updated;
                    });
                    // Also update signals state for the edit modal
                    // But check if we just saved signals - if so, use those values instead of API values
                    setSignals({
                      competitor_price_index: shouldUseSavedValues && currentSavedSignals.competitor_price_index != null
                        ? String(currentSavedSignals.competitor_price_index)
                        : (signalsData.competitor_price_index != null 
                            ? String(signalsData.competitor_price_index) 
                            : ''),
                      event_weather_index: shouldUseSavedValues && currentSavedSignals.event_weather_index != null
                        ? String(currentSavedSignals.event_weather_index)
                        : (signalsData.event_weather_index != null 
                            ? String(signalsData.event_weather_index) 
                            : ''),
                    });
                  } else {
                    console.warn('📊 Signals fetch failed or no data (else branch, non-blocking):', signalsResult);
                  }
                })
                .catch((e) => {
                  console.warn('📊 Failed to load signals for summary (else branch, non-blocking):', e);
                });
            }
          } else {
            setError(result.message || 'Failed to load pricing dashboard');
          }
        } else {
          setError(result.message || 'Failed to load pricing dashboard');
        }
      }
    } catch (err) {
      console.error('📊 loadDashboard Error:', err);
      setError(err?.message || 'Failed to load data');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSite, selectedDishId, period, selectedDate, startDate, endDate, checkPricingRules]); // Removed justSavedSignals - using ref instead

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Update recent price changes with dish names after dishes are loaded
  useEffect(() => {
    if (!data || !dishes.length || !data.recent_price_changes?.length) return;
    
    // Create dish ID to name mapping
    const dishIdToName = {};
    dishes.forEach(dish => {
      const id = dish.dish_id ?? dish.id ?? dish._id;
      const name = dish.dish_name ?? dish.name;
      if (id && name) {
        dishIdToName[id] = name;
      }
    });
    
    if (Object.keys(dishIdToName).length === 0) {
      // No dishes loaded yet, skip update
      return;
    }
    
    // Check if any recent changes need dish name updates
    const needsUpdate = data.recent_price_changes.some(change => {
      const dishId = change.dish_id ?? change.id ?? change.dish;
      const currentName = change.dish_name;
      // If we have a dish ID but no name (or name is the ID or looks like an ID), and we have the name in mapping
      return dishId && (!currentName || currentName === dishId || currentName === String(dishId)) && dishIdToName[dishId];
    });
    
    if (needsUpdate) {
      
      setData(prevData => {
        if (!prevData) return prevData;
        
        const updatedChanges = prevData.recent_price_changes.map(change => {
          const dishId = change.dish_id ?? change.id ?? change.dish;
          const currentName = change.dish_name;
          
          // Normalize IDs for comparison (handle both string and number IDs)
          const normalizedDishId = dishId ? String(dishId) : null;
          
          // If name is missing or is the ID (or looks like an ID), try to get it from mapping
          if (normalizedDishId && (!currentName || currentName === dishId || currentName === normalizedDishId)) {
            // Try to find dish name in mapping (check both string and original ID format)
            const dishName = dishIdToName[dishId] ?? dishIdToName[normalizedDishId];
            if (dishName && dishName !== dishId && dishName !== normalizedDishId) {
              return { ...change, dish_name: dishName, dish_id: dishId };
            }
          }
          
          return change;
        });
        
        return {
          ...prevData,
          recent_price_changes: updatedChanges,
        };
      });
    }
  }, [data, dishes]);

  // Load current signals when site changes (but not while modal is open to prevent overwriting user input)
  const loadSignals = useCallback(async () => {
    if (!selectedSite || signalsEditOpen) return; // Don't reload if modal is open
    
    // Check if we just saved signals - if so, skip loading to prevent overwrite
    if (justSavedSignalsRef.current) {
      return;
    }
    
    setSignalsLoading(true);
    try {
      const result = await getPricingSignals({ site_id: selectedSite });
      if (result.success && result.data) {
        // API returns signals in data.current, not data directly
        const signalsData = result.data.current || result.data;
        
        // Double-check ref again (in case it was set during the async call)
        const currentSavedSignals = justSavedSignalsRef.current;
        if (currentSavedSignals) {
          return;
        }
        
        setSignals({
          competitor_price_index: signalsData.competitor_price_index != null ? String(signalsData.competitor_price_index) : '',
          event_weather_index: signalsData.event_weather_index != null ? String(signalsData.event_weather_index) : '',
        });
        // Also update the summary in data if signals are available
        // But check if we just saved signals - if so, use those values instead of API values
        if (data) {
          setData((prevData) => {
            if (!prevData) return prevData;
            // Check if we just saved signals - if so, use those values instead of API values
            // Use ref to get latest value
            const currentSavedSignals = justSavedSignalsRef.current;
            const shouldUseSavedValues = currentSavedSignals && 
              (currentSavedSignals.competitor_price_index != null || currentSavedSignals.event_weather_index != null);
            
            
            return {
              ...prevData,
              summary: {
                ...prevData.summary,
                competitor_price_index: shouldUseSavedValues && currentSavedSignals.competitor_price_index != null
                  ? currentSavedSignals.competitor_price_index
                  : (signalsData.competitor_price_index != null 
                      ? signalsData.competitor_price_index 
                      : prevData.summary.competitor_price_index),
                event_weather_index: shouldUseSavedValues && currentSavedSignals.event_weather_index != null
                  ? currentSavedSignals.event_weather_index
                  : (signalsData.event_weather_index != null 
                      ? signalsData.event_weather_index 
                      : prevData.summary.event_weather_index),
              },
            };
          });
        }
      }
    } catch (e) {
      console.error('Failed to load signals:', e);
    } finally {
      setSignalsLoading(false);
    }
  }, [selectedSite, signalsEditOpen, data]); // Removed justSavedSignals - using ref instead

  useEffect(() => {
    if (!signalsEditOpen) {
      loadSignals();
    } else {
      // When modal opens, ensure we have the latest signals
      // Load in background without blocking modal opening
      if (selectedSite && !signalsLoading) {
        loadSignals();
      }
    }
  }, [loadSignals, signalsEditOpen, selectedSite, signalsLoading]);

  // Refresh signals when site changes (to get latest signals for the new site)
  useEffect(() => {
    if (selectedSite && !signalsEditOpen) {
      // Clear saved signals ref when site changes to allow fetching new signals
      justSavedSignalsRef.current = null;
      signalsSaveTimeRef.current = null;
      loadSignals();
    }
  }, [selectedSite, signalsEditOpen, loadSignals]);

  // Refresh signals when dashboard becomes visible (e.g., after updating signals in PricingRulesScreen)
  // Use a debounce to prevent multiple rapid refreshes
  useEffect(() => {
    let refreshTimeout = null;
    
    const handleVisibilityChange = () => {
      if (!document.hidden && selectedSite && !signalsEditOpen) {
        // Clear any pending refresh
        if (refreshTimeout) {
          clearTimeout(refreshTimeout);
        }
        
        // Debounce the refresh to avoid rapid calls
        refreshTimeout = setTimeout(() => {
          // Don't clear the ref if signals were just saved (within last 10 seconds)
          // This prevents overwriting signals that were just updated
          const saveTime = signalsSaveTimeRef.current;
          const timeSinceSave = saveTime ? Date.now() - saveTime : Infinity;
          
          if (timeSinceSave > 10000) {
            // Only clear and refresh if signals weren't just saved
            justSavedSignalsRef.current = null;
            signalsSaveTimeRef.current = null;
            loadSignals();
          }
        }, 500); // 500ms debounce
      }
    };

    const handleFocus = () => {
      if (selectedSite && !signalsEditOpen) {
        // Clear any pending refresh
        if (refreshTimeout) {
          clearTimeout(refreshTimeout);
        }
        
        // Debounce the refresh to avoid rapid calls
        refreshTimeout = setTimeout(() => {
          // Don't clear the ref if signals were just saved (within last 10 seconds)
          const saveTime = signalsSaveTimeRef.current;
          const timeSinceSave = saveTime ? Date.now() - saveTime : Infinity;
          
          if (timeSinceSave > 10000) {
            // Only clear and refresh if signals weren't just saved
            justSavedSignalsRef.current = null;
            signalsSaveTimeRef.current = null;
            loadSignals();
          }
        }, 500); // 500ms debounce
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [selectedSite, signalsEditOpen, loadSignals]);

  const handleSaveSignals = async () => {
    setSignalsSaving(true);
    setSignalsMessage(null);
    try {
      const result = await updatePricingSignals({
        site_id: selectedSite || undefined,
        competitor_price_index: signals.competitor_price_index === '' ? undefined : Number(signals.competitor_price_index),
        event_weather_index: signals.event_weather_index === '' ? undefined : Number(signals.event_weather_index),
      });
      if (result.success) {
        setSignalsMessage('Signals updated successfully');
        
        // Get the values we just saved
        const competitorValue = signals.competitor_price_index === '' ? null : Number(signals.competitor_price_index);
        const eventValue = signals.event_weather_index === '' ? null : Number(signals.event_weather_index);
        
        // Store the saved values to prevent them from being overwritten by API fetches
        const savedSignals = {
          competitor_price_index: competitorValue,
          event_weather_index: eventValue,
        };
        setJustSavedSignals(savedSignals);
        justSavedSignalsRef.current = savedSignals; // Also store in ref for closures
        signalsSaveTimeRef.current = Date.now(); // Record when signals were saved
        skipNextDashboardLoadRef.current = true; // Skip next dashboard load to prevent overwrite
        
        // Update summary immediately before closing modal
        setData((prevData) => {
          if (!prevData) return prevData;
          return {
            ...prevData,
            summary: {
              ...prevData.summary,
              competitor_price_index: competitorValue,
              event_weather_index: eventValue,
            },
          };
        });
        
        // Close modal AFTER setting refs to ensure they're available when loadSignals runs
        // Clear the saved signals ref after a delay to allow future updates from API
        // This ensures that if signals are updated elsewhere (e.g., PricingRulesScreen), 
        // the dashboard can fetch and display the new values
        setTimeout(() => {
          justSavedSignalsRef.current = null;
          signalsSaveTimeRef.current = null;
        }, 5000); // Clear after 5 seconds
        
        // Close modal immediately - ref is already set synchronously above
        setSignalsEditOpen(false);
        
        // Don't reload dashboard immediately - it will fetch signals and might overwrite our saved values
        // The summary is already updated above with the saved values
        // We'll keep the saved values in justSavedSignals for a longer period to prevent overwrites
        
        // Clear the justSavedSignals flag after a longer delay to allow API to catch up
        // This ensures our saved values are preserved even if loadDashboard is called
        setTimeout(() => {
          setJustSavedSignals(null);
          justSavedSignalsRef.current = null; // Also clear the ref
          signalsSaveTimeRef.current = null; // Clear the timestamp
          skipNextDashboardLoadRef.current = false; // Allow dashboard loads again
          // After clearing the flag, optionally reload dashboard to get fresh data
          // But only if the API has had time to update
          // loadDashboard();
        }, 30000); // Keep saved values for 30 seconds - gives API plenty of time to update
      } else {
        setSignalsMessage(result.message || 'Failed to update signals');
      }
    } catch (e) {
      setSignalsMessage(e?.message || 'Failed to update signals');
    } finally {
      setSignalsSaving(false);
    }
  };

  const handleRefreshPrices = async () => {
    setRefreshLoading(true);
    try {
      console.log('🔄 Refreshing prices for site:', selectedSite);
      const result = await refreshPricesNow({ site_id: selectedSite || undefined });
      console.log('🔄 refreshPricesNow API Response:', {
        site_id: selectedSite || undefined,
        success: result.success,
        data: result.data,
        message: result.message,
      });
      if (result.success) {
        // Wait a moment for backend to process, then reload
        await new Promise(resolve => setTimeout(resolve, 1000));
        await loadDashboard();
      } else {
        console.warn('Refresh prices:', result.message);
        await loadDashboard();
      }
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleOpenPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const result = await getPricingPreview({ 
        site_id: selectedSite || undefined,
        dish_id: selectedDishId || undefined,
      });
      // console.log('👁️ getPricingPreview API Response:', {
      //   site_id: selectedSite || undefined,
      //   success: result.success,
      //   data: result.data,
      //   message: result.message,
      //   fullResult: result,
      // });
      if (result.success && result.data) setPreviewData(result.data);
      else if (result.data) setPreviewData(result.data);
    } finally {
      setPreviewLoading(false);
    }
  };

  const summary = data?.summary || {};
  const priceSeries = data?.price_series;
  const demandSeries = data?.demand_series;
  const recentChanges = data?.recent_price_changes || [];
  
  // Check if there's any valid data at all
  const hasValidPriceData = priceSeries && (
    (priceSeries.current?.some(v => v != null && v > 0)) ||
    (priceSeries.suggested?.some(v => v != null && v > 0)) ||
    (priceSeries.base?.some(v => v != null && v > 0))
  );
  
  const hasValidDemandData = demandSeries && (
    (demandSeries.demand_intensity?.some(v => v != null)) ||
    (demandSeries.forecast_demand?.some(v => v != null))
  );
  
  const hasAnyData = hasValidPriceData || hasValidDemandData || recentChanges.length > 0;
  
  // Calculate avg_current_vs_base_percent if not provided by API
  // Use current and base prices from price series
  let calculatedAvgPercent = summary.avg_current_vs_base_percent;
  if (calculatedAvgPercent == null && priceSeries?.current && priceSeries?.base) {
    const currentPrices = priceSeries.current.filter(v => v != null && v > 0);
    const basePrices = priceSeries.base.filter(v => v != null && v > 0);
    
    if (currentPrices.length > 0 && basePrices.length > 0) {
      // Calculate average current and base prices
      const avgCurrent = currentPrices.reduce((sum, val) => sum + val, 0) / currentPrices.length;
      const avgBase = basePrices.reduce((sum, val) => sum + val, 0) / basePrices.length;
      
      if (avgBase > 0) {
        calculatedAvgPercent = ((avgCurrent - avgBase) / avgBase) * 100;
      }
    }
  }
  
  // Update summary with calculated value if API didn't provide it
  const enhancedSummary = {
    ...summary,
    avg_current_vs_base_percent: calculatedAvgPercent,
  };

  const lastRunAt = summary.last_run_at
    ? (() => {
        const d = new Date(summary.last_run_at);
        const mins = Math.round((Date.now() - d.getTime()) / 60000);
        return mins < 60 ? `${mins} min ago` : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      })()
    : '—';

  const priceChartData = useMemo(() => {
    // Always return chart data, even if empty - Chart.js will render empty chart
    const labels = priceSeries?.labels?.length 
      ? (priceSeries.labels.length === 1 
          ? [...priceSeries.labels, priceSeries.labels[0]]
          : priceSeries.labels)
      : ['No data'];
    
    // Check if there's any valid price data
    const hasValidPriceData = priceSeries && (
      (priceSeries.current?.some(v => v != null && v > 0)) ||
      (priceSeries.suggested?.some(v => v != null && v > 0)) ||
      (priceSeries.base?.some(v => v != null && v > 0))
    );
    
    if (!hasValidPriceData) {
      console.log('📊 Price chart: No valid price data available, showing empty chart', { priceSeries });
      // Return empty chart data structure
      return {
        labels,
        datasets: [
          {
            label: 'Current price',
            data: labels.map(() => null),
            borderColor: '#0ea5e9',
            backgroundColor: 'rgba(14, 165, 233, 0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 0,
          },
          {
            label: 'Suggested price',
            data: labels.map(() => null),
            borderColor: '#8b5cf6',
            backgroundColor: 'rgba(139, 92, 246, 0.08)',
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 0,
          },
          {
            label: 'Base price',
            data: labels.map(() => null),
            borderColor: '#64748b',
            borderDash: [5, 5],
            fill: false,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 0,
          },
        ],
      };
    }
    
    // Always show all three price lines (Chart.js will handle null values gracefully)
    const base = priceSeries.base ?? priceSeries.labels.map(() => priceSeries.base?.[0] ?? null);
    
    const duplicateIfSingle = (arr) => {
      if (arr.length === 1) {
        return [...arr, arr[0]];
      }
      return arr;
    };
    
    const chartData = {
      labels,
      datasets: [
        {
          label: 'Current price',
          data: duplicateIfSingle(priceSeries.current || []),
          borderColor: '#0ea5e9',
          backgroundColor: 'rgba(14, 165, 233, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: labels.length === 2 ? 4 : 3,
          pointHoverRadius: 6,
          // Chart.js will skip null/undefined values but still show the line in legend
        },
        {
          label: 'Suggested price',
          data: duplicateIfSingle(priceSeries.suggested || []),
          borderColor: '#8b5cf6',
          backgroundColor: 'rgba(139, 92, 246, 0.08)',
          fill: true,
          tension: 0.4,
          pointRadius: labels.length === 2 ? 4 : 3,
          pointHoverRadius: 6,
        },
        {
          label: 'Base price',
          data: duplicateIfSingle(Array.isArray(base) ? base : priceSeries.labels.map(() => base)),
          borderColor: '#64748b',
          borderDash: [5, 5],
          fill: false,
          tension: 0.4,
          pointRadius: labels.length === 2 ? 4 : 3,
          pointHoverRadius: 6,
        },
      ],
    };
    console.log('📊 Price chart data created:', {
      labelsCount: chartData.labels.length,
      currentDataCount: chartData.datasets[0].data.length,
      currentValidData: chartData.datasets[0].data.filter(v => v != null).length,
      suggestedDataCount: chartData.datasets[1].data.length,
      suggestedValidData: chartData.datasets[1].data.filter(v => v != null).length,
      baseDataCount: chartData.datasets[2].data.length,
      baseValidData: chartData.datasets[2].data.filter(v => v != null).length,
    });
    return chartData;
  }, [priceSeries, data?.summary?.last_run_at, priceSeries?._previewUpdatedAt]); // Add preview update timestamp to force re-render

  const demandChartData = useMemo(() => {
    // Always return chart data, even if empty - Chart.js will render empty chart
    const labels = demandSeries?.labels?.length
      ? (demandSeries.labels.length === 1 
          ? [...demandSeries.labels, demandSeries.labels[0]]
          : demandSeries.labels)
      : ['No data'];
    
    // Check if there's any valid demand data
    const hasValidDemandData = demandSeries && (
      (demandSeries.demand_intensity?.some(v => v != null)) ||
      (demandSeries.forecast_demand?.some(v => v != null))
    );
    
    if (!hasValidDemandData) {
      console.log('📊 Demand chart: No valid demand data available, showing empty chart', { demandSeries });
      // Return empty chart data structure
      return {
        labels,
        datasets: [
          {
            label: 'Demand intensity',
            data: labels.map(() => null),
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.15)',
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 0,
          },
          {
            label: 'Forecast demand',
            data: labels.map(() => null),
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 0,
          },
        ],
      };
    }
    
    const duplicateIfSingle = (arr) => {
      if (arr.length === 1) {
        return [...arr, arr[0]];
      }
      return arr;
    };
    
    let demandIntensityData = duplicateIfSingle(demandSeries.demand_intensity ?? demandSeries.demand ?? []);
    
    // If we have very few valid data points (less than 2), try to create a line by:
    // 1. Using forecast values as fallback for missing demand intensity
    // 2. Or duplicating the single valid point
    const validDemandCount = demandIntensityData.filter(v => v != null).length;
    if (validDemandCount < 2 && demandIntensityData.length > 0) {
      const forecastData = demandSeries.forecast_demand ?? demandSeries.forecast ?? [];
      const forecastArray = duplicateIfSingle(Array.isArray(forecastData) ? forecastData : []);
      
      // Use forecast as fallback for null demand intensity values
      demandIntensityData = demandIntensityData.map((val, idx) => {
        if (val != null) return val;
        // Use forecast value if available
        if (forecastArray[idx] != null) return forecastArray[idx];
        return null;
      });
      
      // If still less than 2 valid points, find the single valid point and duplicate it
      const validIndices = demandIntensityData.map((v, i) => v != null ? i : -1).filter(i => i >= 0);
      if (validIndices.length === 1) {
        const validIdx = validIndices[0];
        const validValue = demandIntensityData[validIdx];
        // Duplicate to the next position if available
        if (validIdx < demandIntensityData.length - 1) {
          demandIntensityData[validIdx + 1] = validValue;
        } else if (validIdx > 0) {
          demandIntensityData[validIdx - 1] = validValue;
        }
      }
    }
    
    const datasets = [
      {
        label: 'Demand intensity',
        data: demandIntensityData,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.15)',
        fill: true,
        tension: 0.4,
        pointRadius: labels.length === 2 ? 4 : 3,
        pointHoverRadius: 6,
      },
    ];

    // Always add forecast dataset (Chart.js will handle null values gracefully)
    const forecastData = demandSeries.forecast_demand ?? demandSeries.forecast;
    console.log('📊 Forecast data for chart:', {
      forecastData,
      isArray: Array.isArray(forecastData),
      length: forecastData?.length,
      hasValidData: forecastData?.some(v => v != null),
      validCount: forecastData?.filter(v => v != null).length,
      firstValue: forecastData?.[0],
      allValues: forecastData,
    });
    
    // Always add forecast dataset even if all nulls (so legend shows)
    // Chart.js will skip null values but still show the line in legend
    datasets.push({
      label: 'Forecast demand',
      data: duplicateIfSingle(forecastData && Array.isArray(forecastData) ? forecastData : []),
      borderColor: '#f59e0b',
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      fill: true,
      tension: 0.4,
      pointRadius: labels.length === 2 ? 4 : 3,
      pointHoverRadius: 6,
      // Chart.js will skip null/undefined values but still show the line
    });

    const chartData = {
      labels,
      datasets,
    };
    console.log('📊 Demand chart data created:', {
      labelsCount: chartData.labels.length,
      datasetsCount: chartData.datasets.length,
      demandDataCount: chartData.datasets[0].data.length,
      demandValidData: chartData.datasets[0].data.filter(v => v != null).length,
      hasForecast: chartData.datasets.length > 1,
    });
    return chartData;
  }, [demandSeries, data?.summary?.last_run_at]); // Add last_run_at as dependency to force update

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 750,
      },
      interaction: { intersect: false, mode: 'index' },
      plugins: {
        legend: { display: true, position: 'top' },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx) => {
              if (ctx.parsed.y == null) return `${ctx.dataset.label}: No data`;
              return `${ctx.dataset.label}: ${typeof ctx.parsed.y === 'number' ? (ctx.parsed.y >= 1 ? '₹' + ctx.parsed.y : ctx.parsed.y) : ctx.parsed.y}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: true, color: 'rgba(0,0,0,0.05)' },
          ticks: { maxRotation: 45, font: { size: 11 } },
          // For single point, ensure it's visible
          min: undefined,
          max: undefined,
        },
        y: {
          beginAtZero: false, // Don't force zero start for better visibility of single points
          grid: { display: true, color: 'rgba(0,0,0,0.05)' },
          ticks: { font: { size: 11 } },
          // Allow chart to auto-scale based on data
          min: undefined,
          max: undefined,
        },
      },
      // Ensure points are visible even with single data point
      elements: {
        point: {
          radius: 4,
          hoverRadius: 6,
        },
      },
    }),
    []
  );

  return (
    <div className="w-full h-full">
      {/* Header + Filters */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`${typography.h2} text-black mb-1`}>Pricing Dashboard</h1>
          <p className={`${typography.p} text-gray-600 text-sm`}>
            Dynamic demand-based pricing: signals, forecast, and suggested prices
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            disabled={isSitesLoading}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm min-w-[180px]"
          >
            <option value="">All Sites</option>
            {sites.map((s) => (
              <option key={s.site_id || s.id} value={s.site_id || s.id}>
                {s.site_name || s.name || s.site_id}
              </option>
            ))}
          </select>

          <select
            value={selectedDishId}
            onChange={(e) => setSelectedDishId(e.target.value)}
            disabled={dishesLoading || !selectedSite}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm min-w-[180px]"
            title="Filter charts by dish (per-dish analytics)"
          >
            <option value="">All dishes</option>
            {dishes.map((d) => (
              <option key={d.dish_id || d.id} value={d.dish_id || d.id}>
                {d.dish_name ?? d.name ?? d.dish_id ?? d.id}
              </option>
            ))}
          </select>

          <select
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
              if (e.target.value !== 'custom') {
                setStartDate(null);
                setEndDate(null);
              }
            }}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {period !== 'custom' ? (
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
            />
          ) : (
            <>
              <input
                type="date"
                value={startDate ? (startDate instanceof Date ? formatDateForAPI(startDate) : startDate) : ''}
                max={endDate ? (endDate instanceof Date ? formatDateForAPI(endDate) : endDate) : undefined}
                onChange={(e) => setStartDate(e.target.value || null)}
                className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
              />
              <span className="text-gray-500 text-sm">to</span>
              <input
                type="date"
                value={endDate ? (endDate instanceof Date ? formatDateForAPI(endDate) : endDate) : ''}
                min={startDate ? (startDate instanceof Date ? formatDateForAPI(startDate) : startDate) : undefined}
                onChange={(e) => setEndDate(e.target.value || null)}
                className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
              />
            </>
          )}

          {/* ⚠️ COMMENTED OUT - Refresh prices button
          Backend operates in analytics-only mode and does not actually apply price updates.
          Button is disabled until backend implements actual price application.
          */}
          {/* <button
            type="button"
            onClick={handleRefreshPrices}
            disabled={refreshLoading || isLoading}
            className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm font-medium disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${refreshLoading ? 'animate-spin' : ''}`} />
            Refresh prices now
          </button> */}
          {/* <button
            type="button"
            onClick={handleOpenPreview}
            className="h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-2 text-sm font-medium"
          >
            <Eye className="w-4 h-4" />
            Preview suggested prices
          </button> */}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          ⚠️ {error}
        </div>
      )}


      {!isLoading && !error && !data && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm">
          ℹ️ No pricing data available. The API may not have data for the selected site and date.
          {selectedSite && (
            <span className="block mt-1">
              Try clicking "Refresh prices now" to trigger the pricing engine to process your rules.
            </span>
          )}
        </div>
      )}

      {!isLoading && !error && data && !hasAnyData && (
        <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          ⚠️ No pricing data available for the selected {selectedDishId ? 'dish' : 'site'}.
          {selectedDishId && (
            <span className="block mt-1">
              This dish may not have any pricing rules or historical data. Try selecting a different dish or create pricing rules for this dish.
            </span>
          )}
          {!selectedDishId && selectedSite && (
            <span className="block mt-1">
              Try clicking "Refresh prices now" to trigger the pricing engine to process your rules.
            </span>
          )}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card variant="solid" className="p-4">
          <p className={`${typography.sh3} text-gray-500 mb-1`}>Dishes with auto-pricing</p>
          <p className={`${typography.h4} text-black`}>
            {isLoading ? '—' : summary.dishes_auto_pricing_count ?? 0}
          </p>
        </Card>
        <Card variant="solid" className="p-4">
          <p className={`${typography.sh3} text-gray-500 mb-1`}>Avg current vs base</p>
          <p className={`${typography.h4} text-black flex items-center gap-1`}>
            {isLoading ? '—' : (
              <>
                {enhancedSummary.avg_current_vs_base_percent != null ? (
                  <>
                    <span className={enhancedSummary.avg_current_vs_base_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                      {enhancedSummary.avg_current_vs_base_percent >= 0 ? '+' : ''}
                      {Number(enhancedSummary.avg_current_vs_base_percent).toFixed(1)}%
                    </span>
                    <span className="text-gray-500 text-sm">vs base</span>
                  </>
                ) : (
                  '—'
                )}
              </>
            )}
          </p>
        </Card>
        <Card variant="solid" className="p-4">
          <p className={`${typography.sh3} text-gray-500 mb-1`}>Prices updated</p>
          <p className={`${typography.h4} text-black`}>{isLoading ? '—' : lastRunAt}</p>
        </Card>
        <Card variant="solid" className="p-4">
          <p className={`${typography.sh3} text-gray-500 mb-1`}>Demand intensity</p>
          <p className={`${typography.h4} text-black flex items-center gap-1`}>
            {isLoading ? '—' : (
              <>
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                {summary.current_demand_intensity != null ? (
                  `${summary.current_demand_intensity} (${demandIntensityLabel(summary.current_demand_intensity)})`
                ) : (
                  '—'
                )}
              </>
            )}
          </p>
        </Card>
        <Card variant="solid" className="p-4 relative">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className={`${typography.sh3} text-gray-500 mb-1`}>Competitor / Event index</p>
              <p className={`${typography.p} text-black text-sm`}>
                {isLoading ? '—' : (
                  <>
                    {summary.competitor_price_index != null && `Comp: ${summary.competitor_price_index}`}
                    {summary.competitor_price_index != null && summary.event_weather_index != null && ' · '}
                    {summary.event_weather_index != null && `Event: ${summary.event_weather_index}`}
                    {summary.competitor_price_index == null && summary.event_weather_index == null && '—'}
                  </>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                // Open modal immediately - signals are already loaded by loadSignals
                // If signals need refreshing, loadSignals will handle it when modal closes
                setSignalsEditOpen(true);
              }}
              className="ml-2 p-1.5 text-gray-500 hover:text-black hover:bg-gray-100 rounded transition-colors"
              title="Edit signals"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <Card variant="solid" className="p-4">
          <h3 className={`${typography.sh2} text-black mb-4`}>
            Current vs suggested vs base price
          </h3>
          <div className="h-[280px]">
            {isLoading ? (
              <div className="h-full flex items-center justify-center text-gray-400">Loading…</div>
            ) : priceChartData ? (
              <Line 
                key={`price-chart-${data?.summary?.last_run_at || Date.now()}-${data?.price_series?._previewUpdatedAt || ''}`}
                data={priceChartData} 
                options={chartOptions} 
              />
            ) : null}
          </div>
        </Card>

        <Card variant="solid" className="p-4">
          <h3 className={`${typography.sh2} text-black mb-4`}>
            Demand intensity & forecast
          </h3>
          <div className="h-[280px]">
            {isLoading ? (
              <div className="h-full flex items-center justify-center text-gray-400">Loading…</div>
            ) : demandChartData ? (
              <Line 
                key={`demand-chart-${data?.summary?.last_run_at || Date.now()}`}
                data={demandChartData} 
                options={chartOptions} 
              />
            ) : null}
          </div>
        </Card>
      </div>

      {/* Recent price changes table */}
      <Card variant="solid" className="p-4">
        <h3 className={`${typography.sh2} text-black mb-4`}>Recent price changes</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-600">
                <th className="pb-2 pr-4 font-medium">Dish</th>
                <th className="pb-2 pr-4 font-medium">Old price</th>
                <th className="pb-2 pr-4 font-medium">New price</th>
                <th className="pb-2 pr-4 font-medium">Time</th>
                <th className="pb-2 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : recentChanges.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-gray-400">
                    No recent price changes
                  </td>
                </tr>
              ) : (
                recentChanges.map((row, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-black">{row.dish_name ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-600">{row.old_price != null ? `₹${row.old_price}` : '—'}</td>
                    <td className="py-2 pr-4 text-black font-medium">{row.new_price != null ? `₹${row.new_price}` : '—'}</td>
                    <td className="py-2 pr-4 text-gray-600">{row.time ?? '—'}</td>
                    <td className="py-2 text-gray-600">{row.reason ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Preview modal — rendered in viewport via portal so it sits above layout */}
      {previewOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center min-h-screen w-full overflow-y-auto p-4 bg-black/50"
            onClick={() => setPreviewOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pricing-preview-title"
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <h2 id="pricing-preview-title" className={`${typography.h4} text-black`}>
                  Preview suggested prices
                </h2>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(false)}
                  className="text-gray-500 hover:text-black p-1 rounded hover:bg-gray-100"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="p-4 overflow-auto flex-1 min-h-0">
                {previewLoading ? (
                  <p className="text-gray-500">Loading preview…</p>
                ) : previewData?.items?.length ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 text-left text-gray-600">
                        <th className="pb-2 pr-4 font-medium">Dish</th>
                        <th className="pb-2 pr-4 font-medium">Current</th>
                        <th className="pb-2 pr-4 font-medium">Suggested</th>
                        <th className="pb-2 pr-4 font-medium">Change %</th>
                        <th className="pb-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.items.map((item) => (
                        <tr key={item.dish_id} className="border-b border-gray-100">
                          <td className="py-2 pr-4 text-black">{item.dish_name ?? '—'}</td>
                          <td className="py-2 pr-4">{item.current_price != null ? `₹${item.current_price}` : '—'}</td>
                          <td className="py-2 pr-4 font-medium">{item.suggested_price != null ? `₹${item.suggested_price}` : '—'}</td>
                          <td
                            className={`py-2 pr-4 ${
                              (item.change_percent ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                            }`}
                          >
                            {(item.change_percent ?? 0) >= 0 ? '+' : ''}
                            {Number(item.change_percent ?? 0).toFixed(1)}%
                          </td>
                          <td className="py-2 text-gray-600">{item.reason ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-gray-500">No suggested prices to preview.</p>
                )}
              </div>
              <div className="p-4 border-t border-gray-200 text-sm text-gray-500 flex-shrink-0">
                Suggested prices are for analytics and preview only. Price application is currently off.
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Signals Edit Modal */}
      {signalsEditOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center min-h-screen w-full overflow-y-auto p-4 bg-black/50"
            onClick={() => setSignalsEditOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="signals-edit-title"
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <h2 id="signals-edit-title" className={`${typography.h4} text-black`}>
                  Edit Pricing Signals
                </h2>
                <button
                  type="button"
                  onClick={() => setSignalsEditOpen(false)}
                  className="text-gray-500 hover:text-black p-1 rounded hover:bg-gray-100"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="p-4 overflow-auto flex-1 min-h-0">
                <p className="text-gray-600 text-sm mb-4">
                  Set competitor price index and event/weather index. These values influence the pricing engine calculations.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Competitor price index
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={signals.competitor_price_index ?? ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        // Allow empty string or valid number format
                        if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
                          setSignals(prev => ({
                            ...prev,
                            competitor_price_index: inputValue
                          }));
                        }
                      }}
                      disabled={signalsSaving}
                      placeholder="e.g., 0.8"
                      className="w-full h-10 px-3 bg-white rounded-lg border border-gray-300 text-gray-900 focus:border-green-500 focus:ring-2 focus:ring-green-500/40 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Event / weather index
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={signals.event_weather_index ?? ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        // Allow empty string or valid number format
                        if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
                          setSignals(prev => ({
                            ...prev,
                            event_weather_index: inputValue
                          }));
                        }
                      }}
                      disabled={signalsSaving}
                      placeholder="e.g., 1.2"
                      className="w-full h-10 px-3 bg-white rounded-lg border border-gray-300 text-gray-900 focus:border-green-500 focus:ring-2 focus:ring-green-500/40 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                  {signalsMessage && (
                    <div
                      className={`p-3 rounded-lg text-sm ${
                        signalsMessage.includes('successfully')
                          ? 'bg-green-50 border border-green-200 text-green-800'
                          : 'bg-amber-50 border border-amber-200 text-amber-800'
                      }`}
                    >
                      {signalsMessage}
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setSignalsEditOpen(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveSignals}
                  disabled={signalsSaving}
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {signalsSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DynamicDemandPricingScreen;
